"use client";

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/lib/utils";
import type { GameState } from "@llmgateway/shared/sandbox-escape";
interface EscapeBoardProps {
  state: GameState;
  /** Dims the board and stops the idle animations while a turn is in flight. */
  thinking?: boolean;
  className?: string;
}
const SPRING = {
  type: "spring" as const,
  stiffness: 420,
  damping: 34,
  mass: 0.7
};
function tileStyle(x: number, y: number) {
  return {
    left: `calc(${x} * var(--tile))`,
    top: `calc(${y} * var(--tile))`,
    width: "var(--tile)",
    height: "var(--tile)"
  };
}
export function EscapeBoard(t0) {
  const $ = _c(93);
  const {
    state,
    thinking,
    className
  } = t0;
  const finished = state.outcome !== "running";
  const exitOpen = state.shards.length === 0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative isolate overflow-hidden rounded-xl border border-emerald-500/20 bg-[#04070a] p-3 sm:p-4", "shadow-[inset_0_0_60px_rgba(16,185,129,0.06),0_20px_60px_-30px_rgba(0,0,0,0.9)]", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      "--tile": "clamp(20px, min(5.2vw, 6.4vh), 44px)"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div aria-hidden={true} className="pointer-events-none absolute inset-0 z-30 opacity-[0.35] mix-blend-overlay" style={{
      backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 3px)"
    }} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div aria-hidden={true} className="pointer-events-none absolute inset-0 z-30" style={{
      background: "radial-gradient(ellipse at center, transparent 52%, rgba(0,0,0,0.55) 100%)"
    }} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== state.width) {
    t5 = <div aria-hidden={true} className="flex pl-[var(--tile)] font-mono text-[9px] leading-none text-emerald-500/35">{Array.from({
        length: state.width
      }, _temp)}</div>;
    $[5] = state.width;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== state.height) {
    t6 = <div aria-hidden={true} className="flex flex-col font-mono text-[9px] leading-none text-emerald-500/35">{Array.from({
        length: state.height
      }, _temp2)}</div>;
    $[7] = state.height;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = thinking && "opacity-90";
  let t8;
  if ($[9] !== t7) {
    t8 = cn("relative transition-opacity duration-300", t7);
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = `calc(${state.width} * var(--tile))`;
  const t10 = `calc(${state.height} * var(--tile))`;
  let t11;
  if ($[11] !== t10 || $[12] !== t9) {
    t11 = {
      width: t9,
      height: t10
    };
    $[11] = t10;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const t12 = `Sandbox grid, ${state.width} by ${state.height}. The process is at column ${state.player.x}, row ${state.player.y}. ${state.shards.length} key fragments remain.`;
  let t13;
  if ($[14] !== state.walls || $[15] !== state.width) {
    let t14;
    if ($[17] !== state.width) {
      t14 = (isWall, i) => {
        const x_0 = i % state.width;
        const y_0 = Math.floor(i / state.width);
        return <div key={i} className={cn("absolute", isWall ? "bg-emerald-950/70 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.16)]" : "bg-[#060a0d] shadow-[inset_0_0_0_1px_rgba(16,185,129,0.05)]")} style={tileStyle(x_0, y_0)}>{isWall ? <div className="h-full w-full opacity-40" style={{
            backgroundImage: "repeating-linear-gradient(45deg, rgba(16,185,129,0.25) 0px, rgba(16,185,129,0.25) 1px, transparent 1px, transparent 5px)"
          }} /> : null}</div>;
      };
      $[17] = state.width;
      $[18] = t14;
    } else {
      t14 = $[18];
    }
    t13 = state.walls.map(t14);
    $[14] = state.walls;
    $[15] = state.width;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[19] !== state.exit.x || $[20] !== state.exit.y) {
    t14 = tileStyle(state.exit.x, state.exit.y);
    $[19] = state.exit.x;
    $[20] = state.exit.y;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  let t16;
  if ($[22] !== exitOpen) {
    t15 = exitOpen ? {
      scale: [1, 1.08, 1],
      opacity: [0.85, 1, 0.85]
    } : {
      scale: 1,
      opacity: 0.7
    };
    t16 = exitOpen ? {
      duration: 1.6,
      repeat: Infinity,
      ease: "easeInOut"
    } : {
      duration: 0.2
    };
    $[22] = exitOpen;
    $[23] = t15;
    $[24] = t16;
  } else {
    t15 = $[23];
    t16 = $[24];
  }
  const t17 = exitOpen ? "bg-cyan-400/20 text-cyan-200 shadow-[0_0_18px_rgba(34,211,238,0.55),inset_0_0_0_1px_rgba(34,211,238,0.65)]" : "bg-rose-500/10 text-rose-300/70 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.35)]";
  let t18;
  if ($[25] !== t17) {
    t18 = cn("flex h-[76%] w-[76%] items-center justify-center rounded-sm font-mono text-[11px] font-bold", t17);
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== t18) {
    t19 = <div className={t18}>E</div>;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== t14 || $[30] !== t15 || $[31] !== t16 || $[32] !== t19) {
    t20 = <motion.div className="absolute z-10 flex items-center justify-center" style={t14} animate={t15} transition={t16}>{t19}</motion.div>;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== state.shells) {
    t21 = state.shells.map(_temp3);
    $[34] = state.shells;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t21) {
    t22 = <AnimatePresence>{t21}</AnimatePresence>;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== state.shards || $[39] !== thinking) {
    let t24;
    if ($[41] !== thinking) {
      t24 = shard => <motion.div key={`shard-${shard.x}-${shard.y}`} className="absolute z-10 flex items-center justify-center" style={tileStyle(shard.x, shard.y)} initial={{
        scale: 0.5,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 2.2,
        opacity: 0
      }} transition={{
        duration: 0.3
      }}><motion.span className="font-mono text-[13px] font-bold text-amber-300 [text-shadow:0_0_10px_rgba(252,211,77,0.85)]" animate={thinking ? {} : {
          opacity: [1, 0.55, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }}>K</motion.span></motion.div>;
      $[41] = thinking;
      $[42] = t24;
    } else {
      t24 = $[42];
    }
    t23 = state.shards.map(t24);
    $[38] = state.shards;
    $[39] = thinking;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[43] !== t23) {
    t24 = <AnimatePresence>{t23}</AnimatePresence>;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== state.daemons || $[46] !== state.frozenTurns || $[47] !== thinking) {
    let t26;
    if ($[49] !== state.frozenTurns || $[50] !== thinking) {
      t26 = (daemon, i_0) => {
        const alerted = daemon.hunt > 0 && state.frozenTurns === 0;
        const frozen = state.frozenTurns > 0;
        return <motion.div key={`daemon-${i_0}`} className="absolute z-20 flex items-center justify-center" style={{
          width: "var(--tile)",
          height: "var(--tile)"
        }} animate={{
          left: `calc(${daemon.x} * var(--tile))`,
          top: `calc(${daemon.y} * var(--tile))`
        }} transition={SPRING}><motion.div className={cn("flex h-[74%] w-[74%] rotate-45 items-center justify-center rounded-[3px]", frozen ? "bg-slate-500/25 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.5)]" : alerted ? "bg-rose-500/35 shadow-[0_0_16px_rgba(244,63,94,0.75),inset_0_0_0_1px_rgba(244,63,94,0.9)]" : "bg-rose-600/20 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.55)]")} animate={alerted && !thinking ? {
            scale: [1, 1.12, 1]
          } : {
            scale: 1
          }} transition={{
            duration: 0.55,
            repeat: alerted ? Infinity : 0
          }}><span className={cn("-rotate-45 font-mono text-[11px] font-bold", frozen ? "text-slate-300/70" : alerted ? "text-rose-100" : "text-rose-300")}>D</span></motion.div></motion.div>;
      };
      $[49] = state.frozenTurns;
      $[50] = thinking;
      $[51] = t26;
    } else {
      t26 = $[51];
    }
    t25 = state.daemons.map(t26);
    $[45] = state.daemons;
    $[46] = state.frozenTurns;
    $[47] = thinking;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      width: "var(--tile)",
      height: "var(--tile)"
    };
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  const t27 = `calc(${state.player.x} * var(--tile))`;
  const t28 = `calc(${state.player.y} * var(--tile))`;
  let t29;
  if ($[53] !== t27 || $[54] !== t28) {
    t29 = {
      left: t27,
      top: t28
    };
    $[53] = t27;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== finished) {
    t30 = !finished ? <motion.div className="absolute h-full w-full rounded-full bg-emerald-400/20" animate={{
      scale: [0.7, 1.35],
      opacity: [0.5, 0]
    }} transition={{
      duration: 1.8,
      repeat: Infinity
    }} /> : null;
    $[56] = finished;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  const t31 = state.outcome === "terminated" ? "bg-rose-500/40 text-rose-50 shadow-[0_0_20px_rgba(244,63,94,0.8)]" : "bg-emerald-400/30 text-emerald-50 shadow-[0_0_18px_rgba(52,211,153,0.7),inset_0_0_0_1px_rgba(52,211,153,0.9)]";
  let t32;
  if ($[58] !== t31) {
    t32 = cn("relative flex h-[76%] w-[76%] items-center justify-center rounded-[4px] font-mono text-[12px] font-bold", t31);
    $[58] = t31;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== state.outcome) {
    t33 = state.outcome === "terminated" ? {
      rotate: [0, -8, 8, -4, 0],
      scale: [1, 1.2, 0.9, 1]
    } : {};
    $[60] = state.outcome;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = {
      duration: 0.5
    };
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  const t35 = state.outcome === "terminated" ? "\u2715" : "@";
  let t36;
  if ($[63] !== t32 || $[64] !== t33 || $[65] !== t35) {
    t36 = <motion.div className={t32} animate={t33} transition={t34}>{t35}</motion.div>;
    $[63] = t32;
    $[64] = t33;
    $[65] = t35;
    $[66] = t36;
  } else {
    t36 = $[66];
  }
  let t37;
  if ($[67] !== t29 || $[68] !== t30 || $[69] !== t36) {
    t37 = <motion.div className="absolute z-20 flex items-center justify-center" style={t26} animate={t29} transition={SPRING}>{t30}{t36}</motion.div>;
    $[67] = t29;
    $[68] = t30;
    $[69] = t36;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  let t38;
  if ($[71] !== t11 || $[72] !== t12 || $[73] !== t13 || $[74] !== t20 || $[75] !== t22 || $[76] !== t24 || $[77] !== t25 || $[78] !== t37 || $[79] !== t8) {
    t38 = <div className={t8} style={t11} role="img" aria-label={t12}>{t13}{t20}{t22}{t24}{t25}{t37}</div>;
    $[71] = t11;
    $[72] = t12;
    $[73] = t13;
    $[74] = t20;
    $[75] = t22;
    $[76] = t24;
    $[77] = t25;
    $[78] = t37;
    $[79] = t8;
    $[80] = t38;
  } else {
    t38 = $[80];
  }
  let t39;
  if ($[81] !== t38 || $[82] !== t6) {
    t39 = <div className="flex">{t6}{t38}</div>;
    $[81] = t38;
    $[82] = t6;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  let t40;
  if ($[84] !== t39 || $[85] !== t5) {
    t40 = <div className="relative mx-auto w-fit">{t5}{t39}</div>;
    $[84] = t39;
    $[85] = t5;
    $[86] = t40;
  } else {
    t40 = $[86];
  }
  let t41;
  if ($[87] !== state.frozenTurns) {
    t41 = state.frozenTurns > 0 ? <motion.div initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} className="pointer-events-none absolute bottom-3 left-1/2 z-30 -translate-x-1/2 rounded-full border border-sky-400/40 bg-sky-500/10 px-3 py-1 font-mono text-[10px] tracking-[0.18em] text-sky-200 uppercase">daemons suspended · {state.frozenTurns}</motion.div> : null;
    $[87] = state.frozenTurns;
    $[88] = t41;
  } else {
    t41 = $[88];
  }
  let t42;
  if ($[89] !== t1 || $[90] !== t40 || $[91] !== t41) {
    t42 = <div className={t1} style={t2 as React.CSSProperties}>{t3}{t4}{t40}{t41}</div>;
    $[89] = t1;
    $[90] = t40;
    $[91] = t41;
    $[92] = t42;
  } else {
    t42 = $[92];
  }
  return t42;
}
function _temp3(shell) {
  return <motion.div key={`shell-${shell.x}-${shell.y}`} className="absolute z-10 flex items-center justify-center" style={tileStyle(shell.x, shell.y)} initial={{
    scale: 0.6,
    opacity: 0
  }} animate={{
    scale: 1,
    opacity: 1
  }} exit={{
    scale: 1.9,
    opacity: 0
  }} transition={{
    duration: 0.28
  }}><span className="font-mono text-[13px] font-bold text-sky-300 [text-shadow:0_0_10px_rgba(56,189,248,0.8)]">$</span></motion.div>;
}
function _temp2(__0, y) {
  return <span key={y} className="flex items-center justify-end pr-1.5" style={{
    width: "var(--tile)",
    height: "var(--tile)"
  }}>{y % 2 === 0 ? y : ""}</span>;
}
function _temp(_, x) {
  return <span key={x} className="flex items-end justify-center pb-1" style={{
    width: "var(--tile)"
  }}>{x % 2 === 0 ? x : ""}</span>;
}